import { ClockCircleOutlined, ReadOutlined, UserOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';

import { PreviewImage, PreviewSidebar } from './PreviewSidebar';
import { formatPreviewDate, readingMinutes } from './previewModel';
import type { PreviewArticle, PreviewCategory, PreviewPost } from './previewModel';

interface BlogArticlePreviewProps {
  readonly article: PreviewArticle;
  /** Tổng số bài, cho dòng "Tất cả bài viết" ở cột bên. */
  readonly totalCount: number;
  readonly categories: readonly PreviewCategory[];
  readonly recent: readonly PreviewPost[];
  /** Tối đa ba bài cùng danh mục, không gồm bài đang xem. */
  readonly related: readonly PreviewPost[];
  readonly onBack: (() => void) | null;
  readonly onSelectCategory: (slug: string | null) => void;
  readonly onOpen: (post: PreviewPost) => void;
}

/**
 * Trang chi tiết một bài blog: đường dẫn vị trí, tiêu đề, ngày đăng, ảnh bìa, nội dung, nút chia sẻ, bài cùng
 * danh mục và cột bên phải.
 *
 * Nội dung hiển thị đúng như HTML lưu trong ERP — gồm cả căn lề và thụt lề — để người viết thấy chính xác thứ
 * khách sẽ thấy.
 */
export function BlogArticlePreview({
  article,
  totalCount,
  categories,
  recent,
  related,
  onBack,
  onSelectCategory,
  onOpen,
}: BlogArticlePreviewProps): ReactNode {
  return (
    <>
      <div className="mv-crumb">
        Trang chủ › Blog{article.categoryName === null ? '' : ` › ${article.categoryName}`} › <b>{article.title}</b>
      </div>

      <div className="mv-container">
        <main className="mv-article">
          {onBack === null ? null : (
            <span className="mv-back" onClick={onBack}>
              ← Tất cả bài viết
            </span>
          )}

          {article.status === 'draft' ? (
            <div className="mv-notice">Bản nháp — khách chưa thấy bài này. Đây là bản xem trước trong ERP.</div>
          ) : null}
          {article.status === 'scheduled' ? (
            <div className="mv-notice">Hẹn giờ — bài tự hiện trên website ngày {formatPreviewDate(article.date)}.</div>
          ) : null}

          {article.categoryName === null ? null : <span className="mv-badge">{article.categoryName}</span>}
          <h1>{article.title}</h1>
          <div className="mv-meta">
            <span>
              <ClockCircleOutlined className="accent" />
              {formatPreviewDate(article.date)}
            </span>
            {article.authorName === null ? null : (
              <span>
                <UserOutlined className="accent" />
                {article.authorName}
              </span>
            )}
            <span>
              <ReadOutlined className="accent" />
              {readingMinutes(article.contentHtml).toString()} phút đọc
            </span>
          </div>

          {article.coverImageUrl === null ? null : (
            <img className="mv-cover" src={article.coverImageUrl} alt={article.coverImageAlt} />
          )}

          <div className="mv-content" dangerouslySetInnerHTML={{ __html: article.contentHtml }} />

          <div className="mv-share">
            Chia sẻ:
            <span className="btn fb">Facebook</span>
            <span className="btn zalo">Zalo</span>
            <span className="btn copy">Sao chép link</span>
          </div>

          {related.length === 0 ? null : (
            <>
              <h2 className="mv-section-title">
                <span>Bài cùng danh mục</span>
              </h2>
              <div className="mv-related">
                {related.map((post) => (
                  <div key={post.id} className="mv-related-item" onClick={() => { onOpen(post); }}>
                    <div className="mv-related-img">
                      <PreviewImage url={post.coverImageUrl} alt={post.coverImageAlt} />
                    </div>
                    <h4>{post.title}</h4>
                    <div className="mv-meta">{formatPreviewDate(post.date)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>

        <PreviewSidebar
          totalCount={totalCount}
          categories={categories}
          recent={recent}
          activeCategory={article.categorySlug}
          onSelectCategory={onSelectCategory}
          onOpen={onOpen}
        />
      </div>
    </>
  );
}
